import { Fragment, useState } from "react"
import { Download, PieChart, Plus, Trash2 } from "lucide-react"
import { Card, Button, NumberControl } from "../../ui"
import { showToastError } from "utilities/toastUtil"
import { formatAmount } from "../../constants"
import {
  BUDGET_ALLOCATION_COLUMNS,
  VALIDATION_LABELS,
  BUDGET_IMPACT_LABELS,
  analysisLabel,
  allocationMonthLabel,
  groupByPo,
  splitByWeight,
  sumBy,
  downloadBudgetAllocationXlsx,
} from "./budgetAllocation"
import { EXTRA_TYPES } from "./create/lineAllocation"
import { HScroll } from "procurement/design/components/HScroll"

const cellText = (col, row) => {
  const v =
    col.key === "analysis" ? analysisLabel(row) : col.key === "monthLabel" ? allocationMonthLabel(row) : row[col.key]
  if (v == null || v === "") return "—"
  if (col.money) return formatAmount(v)
  return v
}

const MONTH_STYLE = {
  OVER_BUDGET: { color: "var(--pc-danger, #b42318)" },
  UNALLOCATED: { color: "var(--pc-warn, #b54708)" },
}

const TOTAL_LABEL_SPAN = BUDGET_ALLOCATION_COLUMNS.findIndex((c) => c.key === "poLineAmount")

const withPct =(amount, percentage) => `${formatAmount(amount)}${percentage != null ? ` (${percentage}%)` : ""}`

const poSummary = (head) => {
  const parts = []
  if (head.poPaymentAmount != null) parts.push(`This payment: ${withPct(head.poPaymentAmount, head.poPaymentPercentage)}`)
  if (head.poPaidAmount != null) parts.push(`Paid: ${withPct(head.poPaidAmount, head.poPaidPercentage)}`)
  const summary = parts.join(" · ")
  return head.poTotalAmount != null ? `${summary}${summary ? " / " : ""}PO total ${formatAmount(head.poTotalAmount)}` : summary
}

const digits = (v) => v.replace(/[^\d]/g, "")
const amountInput = { width: 140, textAlign: "right" }

// Data entry: focus selects the figure so typing replaces it; Enter jumps to the next amount.
const selectOnFocus = (e) => e.target.select()
const nextOnEnter = (e) => {
  if (e.key !== "Enter") return
  e.preventDefault()
  const inputs = [...e.currentTarget.closest("table").querySelectorAll("input[data-amount]")]
  inputs[inputs.indexOf(e.currentTarget) + 1]?.focus()
}

// `editor` (create/edit form only) makes "This payment" typeable per line and
// per extra row: { onLine(purchaseId, orLineId, value), onExtra(purchaseId,
// key, patch), onAddExtra(purchaseId), onRemoveExtra(purchaseId, key),
// statusOf(purchaseId) → { allocated, total, issue, canAddExtra } | null }.
const BudgetAllocationCard = ({ rows, period, fileBase, loading, editor, disabled }) => {
  const [downloading, setDownloading] = useState(false)

  const handleDownload = async () => {
    setDownloading(true)
    try {
      await downloadBudgetAllocationXlsx(rows, fileBase)
    } catch {
      showToastError("Couldn't generate the Excel file. Please try again.")
    } finally {
      setDownloading(false)
    }
  }

  const periodText = period ? `${String(period.month).padStart(2, "0")}/${period.year} · ` : ""

  // A merged "Chưa phân phối" row spreads the typed amount over its OR lines.
  const onRowValue = (r, v) => {
    if (!r.groupLines) {
      editor.onLine(r.purchaseId, r.orLineId, v)
      return
    }
    splitByWeight(Number(v) || 0, r.groupLines.map((g) => g.weight)).forEach((part, i) =>
      editor.onLine(r.purchaseId, r.groupLines[i].orLineId, String(part))
    )
  }

  const renderCell = (c, r) => {
    const isExtra = r.lineType === "EXTRA"
    if (editor && c.key === "value" && isExtra) {
      return (
        <NumberControl
          className="prc-input"
          inputMode="numeric"
          style={amountInput}
          value={r.value ?? ""}
          disabled={disabled}
          aria-label="Extra amount"
          data-amount
          onFocus={selectOnFocus}
          onKeyDown={nextOnEnter}
          onChange={(v) => editor.onExtra(r.purchaseId, r.key, { amount: digits(v) })}
        />
      )
    }
    if (editor && c.key === "value" && r.maxValue != null) {
      return (
        <>
          <NumberControl
            className="prc-input"
            inputMode="numeric"
            style={amountInput}
            value={r.value ?? ""}
            disabled={disabled}
            aria-label={`This payment for ${r.orCode || r.itemName || r.poCode}`}
            data-amount
            onFocus={selectOnFocus}
            onKeyDown={nextOnEnter}
            onChange={(v) => onRowValue(r, digits(v))}
          />
          <div className="pc-muted" style={{ fontSize: 12 }}>
            max: {formatAmount(r.maxValue)}
          </div>
          {r.groupLines && (
            <div className="pc-muted" style={{ fontSize: 12 }}>
              Split across {r.groupLines.length} lines by PO line amount
            </div>
          )}
          {(r.paidByOthers > 0 || r.pendingByOthers > 0) && (
            <div className="pc-muted" style={{ fontSize: 12 }}>
              Paid {formatAmount(r.paidByOthers)} · Pending {formatAmount(r.pendingByOthers)} /{" "}
              {formatAmount(r.poLineAmount)}
            </div>
          )}
        </>
      )
    }
    if (editor && c.key === "itemName" && isExtra) {
      return (
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <select
            className="prc-select"
            value={r.extraType}
            disabled={disabled}
            aria-label="Extra type"
            onChange={(e) => editor.onExtra(r.purchaseId, r.key, { extraType: e.target.value })}
          >
            {EXTRA_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
          <input
            className="prc-input"
            value={r.description}
            placeholder="Description"
            disabled={disabled}
            onChange={(e) => editor.onExtra(r.purchaseId, r.key, { description: e.target.value })}
          />
          <Button
            variant="ghost"
            size="sm"
            aria-label="Remove extra"
            disabled={disabled}
            onClick={() => editor.onRemoveExtra(r.purchaseId, r.key)}
          >
            <Trash2 aria-hidden />
          </Button>
        </div>
      )
    }
    return (
      <>
        {cellText(c, r)}
        {c.key === "remainingAfter" && r.remainingAfter != null && BUDGET_IMPACT_LABELS[r.budgetImpact] && (
          <div className="pc-muted" style={{ fontSize: 12 }}>
            {BUDGET_IMPACT_LABELS[r.budgetImpact]}
          </div>
        )}
        {c.key === "orCode" && VALIDATION_LABELS[r.validationStatus] && (
          <div style={{ fontSize: 12, color: "var(--pc-warn, #b54708)" }}>{VALIDATION_LABELS[r.validationStatus]}</div>
        )}
      </>
    )
  }

  const renderStatus = (purchaseId) => {
    const status = editor?.statusOf(purchaseId)
    if (!status) return null
    return (
      <tr>
        <td colSpan={BUDGET_ALLOCATION_COLUMNS.length}>
          <div style={{ display: "flex", gap: 12, alignItems: "center", justifyContent: "flex-end", flexWrap: "wrap" }}>
            {(status.paidByOthers > 0 || status.pendingByOthers > 0) && (
              <span className="pc-muted">
                Paid by other PRs {formatAmount(status.paidByOthers)} · Pending {formatAmount(status.pendingByOthers)}
              </span>
            )}
            <span style={status.issue ? { color: "var(--pc-danger, #b42318)" } : undefined}>
              {status.issue || `Total: ${formatAmount(status.allocated)}`}
            </span>
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<Plus aria-hidden />}
              disabled={disabled || !status.canAddExtra}
              title={status.canAddExtra ? undefined : "Fill every line up to its max first"}
              onClick={() => editor.onAddExtra(purchaseId)}
            >
              Add extra
            </Button>
          </div>
        </td>
      </tr>
    )
  }

  return (
    <Card
      icon={<PieChart aria-hidden />}
      title="Budget allocation"
      subtitle={`${periodText}${rows.length} line(s)`}
      action={
        <Button
          variant="secondary"
          size="sm"
          leftIcon={<Download aria-hidden />}
          disabled={rows.length === 0 || downloading}
          onClick={handleDownload}
        >
          {downloading ? "Preparing…" : "Download Excel"}
        </Button>
      }
    >
      {loading ? (
        <p className="pc-muted">Loading budget allocation…</p>
      ) : rows.length === 0 ? (
        <p className="pc-muted">No budget allocation yet.</p>
      ) : (
        <>
          <HScroll className="pc-table-wrap">
            <table className="pc-table">
              <thead>
                <tr>
                  {BUDGET_ALLOCATION_COLUMNS.map((c) => (
                    <th key={c.key} className={c.money ? "pc-th-right" : undefined} title={c.hint}>
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {groupByPo(rows).map((g) => (
                  <Fragment key={g.purchaseId || "lines"}>
                    {g.poCode && poSummary(g.head) && (
                      <tr>
                        <td className="pc-td-strong" colSpan={BUDGET_ALLOCATION_COLUMNS.length}>
                          {g.poCode} — {poSummary(g.head)}
                        </td>
                      </tr>
                    )}
                    {g.rows.map((r) => (
                      <tr key={r.key}>
                        {BUDGET_ALLOCATION_COLUMNS.map((c) => (
                          <td
                            key={c.key}
                            className={c.money ? "pc-td-right" : undefined}
                            title={
                              c.key === "monthLabel" && r.unallocatedMonths
                                ? r.unallocatedMonths
                                : c.key === "itemName" && r.itemNames?.length > 1
                                  ? r.itemNames.join(", ")
                                  : undefined
                            }
                            style={{
                              ...(c.key === "monthLabel" ? MONTH_STYLE[r.budgetStatus] : null),
                              whiteSpace: "nowrap",
                              ...((c.key === "remaining" || c.key === "remainingAfter") && r[c.key] < 0
                                ? { color: "var(--pc-danger, #b42318)" }
                                : null),
                            }}
                          >
                            {renderCell(c, r)}
                          </td>
                        ))}
                      </tr>
                    ))}
                    {g.purchaseId && renderStatus(g.purchaseId)}
                  </Fragment>
                ))}
                <tr>
                  <td className="pc-td-strong" colSpan={TOTAL_LABEL_SPAN}>
                    Total
                  </td>
                  <td className="pc-td-right pc-td-strong">{formatAmount(sumBy(rows, "poLineAmount"))}</td>
                  <td className="pc-td-right pc-td-strong">{formatAmount(sumBy(rows, "value"))}</td>
                  <td colSpan={BUDGET_ALLOCATION_COLUMNS.length - TOTAL_LABEL_SPAN - 2} />
                </tr>
              </tbody>
            </table>
          </HScroll>
          {!editor && (
            <p className="pc-muted">
              This payment is this PR’s amount on each PO line. Extra rows are paid on top of the PO lines.
            </p>
          )}
        </>
      )}
    </Card>
  )
}

export default BudgetAllocationCard
